/**
 * meshView — converts kernel MeshData into renderable three.js objects, and
 * provides the shared CAD look: PBR body material + crisp dark edge lines.
 */

import * as THREE from 'three/webgpu';

import type { MeshData } from '@core/geometry/types';

export const BODY_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0xb7bfcc,
  metalness: 0.15,
  roughness: 0.45,
});

export const EDGE_MATERIAL = new THREE.LineBasicMaterial({ color: 0x21242a });

export const SKETCH_MATERIAL = new THREE.LineBasicMaterial({ color: 0x5aa2e8 });

export const SELECTED_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0x9db8d9,
  metalness: 0.15,
  roughness: 0.4,
  emissive: 0x1a4a7a,
  emissiveIntensity: 0.55,
});

/**
 * Line geometries have no normals, which makes the AO pass's MRT normal
 * output warn on every frame. Zero-filled normals keep the pipeline quiet.
 */
export function ensureNormals(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  if (!geo.getAttribute('normal') && geo.getAttribute('position')) {
    const count = geo.getAttribute('position').count;
    geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  }
  return geo;
}

export const CURVATURE_MATERIAL = new THREE.MeshStandardMaterial({
  vertexColors: true,
  metalness: 0,
  roughness: 0.8,
});

/** FEA stress display — vertex-colored, matte so the ramp reads true. */
export const STRESS_MATERIAL = new THREE.MeshStandardMaterial({
  vertexColors: true,
  metalness: 0,
  roughness: 0.75,
});

/** Blue (0) → green → red (1) ramp shared by stress display and legend. */
export function stressRamp(t: number): [number, number, number] {
  const c = Math.min(1, Math.max(0, t));
  return [
    Math.min(1, Math.max(0, c * 2 - 1)) * 0.9 + 0.1,
    (1 - Math.abs(c * 2 - 1)) * 0.85 + 0.1,
    Math.min(1, Math.max(0, 1 - c * 2)) * 0.9 + 0.1,
  ];
}

/**
 * Per-vertex curvature colors for fairing feedback: weld coincident
 * positions, measure how much adjacent face normals disagree at each welded
 * vertex, normalize by local edge length (≈ 1/radius), and map through a
 * blue (flat) → green → red (tight) ramp. Duplicated flat-shaded vertices
 * all receive their weld group's color, so facet seams don't show.
 */
export function curvatureColors(data: MeshData): Float32Array {
  const p = data.positions;
  const idx = data.indices;
  const vcount = p.length / 3;
  const colors = new Float32Array(p.length);

  // Weld by quantized position.
  const weldOf = new Int32Array(vcount);
  const weldIndex = new Map<string, number>();
  let weldCount = 0;
  for (let i = 0; i < vcount; i++) {
    const k = `${Math.round(p[i * 3]! * 1e4)},${Math.round(p[i * 3 + 1]! * 1e4)},${Math.round(p[i * 3 + 2]! * 1e4)}`;
    let w = weldIndex.get(k);
    if (w === undefined) {
      w = weldCount++;
      weldIndex.set(k, w);
    }
    weldOf[i] = w;
  }

  // Accumulate face normals + edge lengths per welded vertex.
  const nx = new Float64Array(weldCount);
  const ny = new Float64Array(weldCount);
  const nz = new Float64Array(weldCount);
  const spread = new Float64Array(weldCount); // max pairwise normal deviation
  const faceNormals: number[][][] = Array.from({ length: weldCount }, () => []);
  const edgeLen = new Float64Array(weldCount);
  const edgeN = new Float64Array(weldCount);

  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t]! * 3, b = idx[t + 1]! * 3, c = idx[t + 2]! * 3;
    const ux = p[b]! - p[a]!, uy = p[b + 1]! - p[a + 1]!, uz = p[b + 2]! - p[a + 2]!;
    const vx = p[c]! - p[a]!, vy = p[c + 1]! - p[a + 1]!, vz = p[c + 2]! - p[a + 2]!;
    let fx = uy * vz - uz * vy, fy = uz * vx - ux * vz, fz = ux * vy - uy * vx;
    const len = Math.hypot(fx, fy, fz);
    if (len < 1e-12) continue;
    fx /= len; fy /= len; fz /= len;
    const e1 = Math.hypot(ux, uy, uz);
    for (const vi of [idx[t]!, idx[t + 1]!, idx[t + 2]!]) {
      const w = weldOf[vi]!;
      nx[w]! += fx; ny[w]! += fy; nz[w]! += fz;
      faceNormals[w]!.push([fx, fy, fz]);
      edgeLen[w]! += e1;
      edgeN[w]! += 1;
    }
  }

  for (let w = 0; w < weldCount; w++) {
    const list = faceNormals[w]!;
    const alen = Math.hypot(nx[w]!, ny[w]!, nz[w]!) || 1;
    const ax = nx[w]! / alen, ay = ny[w]! / alen, az = nz[w]! / alen;
    let maxDev = 0;
    for (const f of list) {
      const dot = Math.max(-1, Math.min(1, f[0]! * ax + f[1]! * ay + f[2]! * az));
      maxDev = Math.max(maxDev, Math.acos(dot));
    }
    spread[w] = maxDev;
  }

  for (let i = 0; i < vcount; i++) {
    const w = weldOf[i]!;
    const avgEdge = edgeN[w]! > 0 ? edgeLen[w]! / edgeN[w]! : 1;
    // Discrete curvature estimate ≈ normal spread per unit length (1/mm).
    const kappa = spread[w]! / Math.max(avgEdge, 1e-6);
    // Ramp: 0 (flat, blue) … 0.35/mm+ (r≈3mm, red), log-ish response.
    const t = Math.min(1, Math.sqrt(kappa / 0.35));
    // blue → green → red
    const r = Math.min(1, Math.max(0, t * 2 - 1));
    const g = 1 - Math.abs(t * 2 - 1);
    const b = Math.min(1, Math.max(0, 1 - t * 2));
    colors[i * 3] = r * 0.9 + 0.1;
    colors[i * 3 + 1] = g * 0.85 + 0.1;
    colors[i * 3 + 2] = b * 0.9 + 0.1;
  }
  return colors;
}

/**
 * Box-projected UVs: each vertex maps its two world coordinates orthogonal
 * to its normal's dominant axis, at 1 UV unit per 25mm. Kernel meshes carry
 * no parameterization — this is what lets procedural material textures
 * (brushed metal streaks) tile over any body.
 */
function boxProjectUVs(geo: THREE.BufferGeometry): void {
  const p = geo.getAttribute('position');
  const n = geo.getAttribute('normal');
  const uv = new Float32Array(p.count * 2);
  const SCALE = 1 / 25;
  for (let i = 0; i < p.count; i++) {
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    if (az >= ax && az >= ay) {
      uv[i * 2] = p.getX(i) * SCALE; uv[i * 2 + 1] = p.getY(i) * SCALE;
    } else if (ax >= ay) {
      uv[i * 2] = p.getY(i) * SCALE; uv[i * 2 + 1] = p.getZ(i) * SCALE;
    } else {
      uv[i * 2] = p.getX(i) * SCALE; uv[i * 2 + 1] = p.getZ(i) * SCALE;
    }
  }
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
}

/** Kernel output → mesh + edge overlay. */
export function meshDataToObject(
  data: MeshData,
  selected = false,
  baseMaterial: THREE.MeshStandardMaterial = BODY_MATERIAL,
): THREE.Group {
  const group = new THREE.Group();
  if (data.positions.length > 0) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
    geo.setIndex(new THREE.BufferAttribute(data.indices, 1));
    if (baseMaterial.map || baseMaterial.roughnessMap) boxProjectUVs(geo);
    const mesh = new THREE.Mesh(geo, selected ? SELECTED_MATERIAL : baseMaterial);
    // Selection restores this instead of the global default (materials).
    mesh.userData.baseMaterial = baseMaterial;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  if (data.edgePositions.length > 0) {
    const edgeGeo = new THREE.BufferGeometry();
    edgeGeo.setAttribute('position', new THREE.BufferAttribute(data.edgePositions, 3));
    group.add(new THREE.LineSegments(ensureNormals(edgeGeo), EDGE_MATERIAL));
  }
  return group;
}

